import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtNum } from "@/api";
import { TrendingUp, TrendingDown, Users, Briefcase, Layers, Activity } from "lucide-react";
import { Link } from "react-router-dom";

const MetricCard = ({ label, value, sub, icon: Icon, tone = "default", testid }) => {
  const toneColor = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-rose-400" : "text-white";
  return (
    <div className="card-tactical p-5 md:p-6 animate-fade-up" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="label-caps">{label}</div>
        <div className="w-9 h-9 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center">
          <Icon size={16} className="text-emerald-400" />
        </div>
      </div>
      <div className={`metric-value text-3xl md:text-4xl mt-3 ${toneColor}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1.5">{sub}</div>}
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pnl = data?.total_unrealized_pnl ?? 0;
  const pnlTone = pnl >= 0 ? "up" : "down";
  const PnlIcon = pnl >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Overview</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-2 tracking-tight">
            Broker Command Terminal
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">
            Aggregated portfolio value, active holdings, and daily trade activity across all client accounts.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-800 bg-slate-900/60">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="label-caps !text-emerald-400">Live</span>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <MetricCard
          label="Total Portfolio Value"
          value={fmtMoney(data?.total_portfolio_value)}
          sub={`Cost basis ${fmtMoney(data?.total_cost_basis)}`}
          icon={Briefcase}
          testid="dashboard-summary-total-portfolio-value"
        />
        <MetricCard
          label="Unrealized P&L"
          value={fmtMoney(pnl)}
          sub={`Realized: ${fmtMoney(data?.total_realized_pnl)}`}
          icon={PnlIcon}
          tone={pnlTone}
          testid="dashboard-summary-unrealized-pnl"
        />
        <MetricCard
          label="Active Clients"
          value={fmtNum(data?.total_clients)}
          sub={`${fmtNum(data?.total_active_holdings)} active holdings`}
          icon={Users}
          testid="dashboard-summary-total-clients-count"
        />
        <MetricCard
          label="Stocks Tracked"
          value={fmtNum(data?.total_stocks_tracked)}
          sub={`${fmtNum(data?.total_transactions)} total trades`}
          icon={Layers}
          testid="dashboard-summary-total-holdings-count"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="card-tactical p-6 lg:col-span-7">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Top Stocks by Portfolio Value</div>
              <h2 className="font-heading font-bold text-xl mt-1">Aggregate Holdings</h2>
            </div>
            <Link to="/stocks" className="text-xs text-emerald-400 hover:underline" data-testid="link-view-all-stocks">View All →</Link>
          </div>
          {data?.top_stocks?.length ? (
            <table className="tactical" data-testid="table-top-stocks">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th className="!text-right">Qty</th>
                  <th className="!text-right">Value</th>
                  <th className="!text-right">Holders</th>
                </tr>
              </thead>
              <tbody>
                {data.top_stocks.map((s) => (
                  <tr key={s.symbol}>
                    <td>
                      <Link to={`/shareholding?symbol=${s.symbol}`} className="font-mono-tabular font-bold text-emerald-400 hover:underline">
                        {s.symbol}
                      </Link>
                    </td>
                    <td className="!text-right font-mono-tabular">{fmtNum(s.quantity, 2)}</td>
                    <td className="!text-right font-mono-tabular">{fmtMoney(s.current_value)}</td>
                    <td className="!text-right font-mono-tabular text-slate-400">{s.num_holders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState label="No holdings yet. Record a trade to see aggregate positions." />
          )}
        </div>

        <div className="card-tactical p-6 lg:col-span-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Recent Trades</div>
              <h2 className="font-heading font-bold text-xl mt-1">Trade Feed</h2>
            </div>
            <Activity size={18} className="text-emerald-400" />
          </div>
          {data?.recent_transactions?.length ? (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2" data-testid="list-recent-transactions">
              {data.recent_transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-800/60">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider ${t.trade_type === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                        {t.trade_type}
                      </span>
                      <span className="font-mono-tabular font-bold text-sm">{t.symbol}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-1 truncate">{t.client_name} • {t.trade_date}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-tabular text-sm">{fmtNum(t.quantity, 2)} @ {fmtMoney(t.price)}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{fmtMoney(t.quantity * t.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState label="No trades recorded yet." />
          )}
        </div>
      </section>
    </div>
  );
}

const EmptyState = ({ label }) => (
  <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-slate-800 rounded-lg">
    {label}
  </div>
);
